import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ViewIcon } from "@/components/icons/action-icons";
import { type ElementType, useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  Clock,
  GitMerge,
  History,
  Hourglass,
  RefreshCw,
  UserIcon,
  XCircle,
} from "lucide-react";
import WorkflowAuditDialog from "./WorkflowAuditDialog";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { type BadgeVariant } from "@/components/ui/badge/badge.variants";
import { buttonVariants } from "@/components/ui/button/buttonVariants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table } from "@/components/table";
import { startCase } from "lodash";
import { useGetWorkflowInstancesByIdQuery } from "@/services/(company)/workflowApi";
import { useLocale } from "@/hooks/useLocale";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  WorkflowInstanceStatus,
  type WorkflowIssueFilter,
} from "@/types/(company)/workflow.interface";
import { ENUM_MODULE_TYPE } from "@/pages/(home)/(company)/workflows/builder_v2/types";
import { PATHS } from "@/routes/paths";
import { COMPANY_STUDIO_PATHS } from "@/routes/company/routes.studio";
import { getCategoryForModule } from "@/pages/(home)/(company)/studio/workflows/_components/workflow-types";
import { withBranding } from "@/utils/helper";

// ─── Columns ──────────────────────────────────────────────────────────────────
/** Notification-issue filter modes (state values; labels are translated). */
const ISSUE_FILTER_OPTIONS = ["hide", "show", "only"] as const;

const buildColumns = (t: TFunction) => [
  { key: "requestedBy", label: t("company:workflows.col_requested_by") },
  {
    key: "currentApprover",
    label: t("company:workflows.col_current_approver"),
  },
  { key: "requestedAt", label: t("company:workflows.col_requested_at") },
  { key: "deadline", label: t("company:workflows.col_deadline") },
  { key: "version", label: t("company:workflows.col_version") },
  { key: "status", label: t("company:workflows.col_status") },
  { key: "actions", label: t("company:workflows.col_actions") },
];

// ─── Status pill ──────────────────────────────────────────────────────────────
const WORKFLOW_STATUS_PILLS: Record<
  WorkflowInstanceStatus | "default",
  { icon: ElementType; variant: BadgeVariant }
> = {
  [WorkflowInstanceStatus.PENDING]: { icon: Hourglass, variant: "warning" },
  [WorkflowInstanceStatus.IN_PROGRESS]: { icon: Clock, variant: "info" },
  [WorkflowInstanceStatus.APPROVED]: { icon: CheckCircle2, variant: "success" },
  [WorkflowInstanceStatus.REJECTED]: { icon: XCircle, variant: "danger" },
  [WorkflowInstanceStatus.CANCELLED]: { icon: Ban, variant: "neutral" },
  [WorkflowInstanceStatus.CHANGES_REQUESTED]: {
    icon: RefreshCw,
    variant: "primary",
  },
  [WorkflowInstanceStatus.OVERDUE]: { icon: AlertTriangle, variant: "danger" },
  [WorkflowInstanceStatus.ERROR]: { icon: AlertOctagon, variant: "danger" },
  default: { icon: Hourglass, variant: "warning" },
};

interface WorkflowStatusPillProps {
  status: WorkflowInstanceStatus | string;
}

export const WorkflowStatusPill = ({ status }: WorkflowStatusPillProps) => {
  const { icon: Icon, variant } =
    WORKFLOW_STATUS_PILLS[status as WorkflowInstanceStatus] ??
    WORKFLOW_STATUS_PILLS.default;

  return (
    <Badge variant={variant} dot={false}>
      <Icon className="size-3" />
      {startCase(status)}
    </Badge>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const WorkflowRequestsTable = () => {
  const { t } = useTranslation(["common", "company"]);
  const { formatDate } = useLocale();
  const columns = buildColumns(t);
  usePageTitle(withBranding(t("company:workflows.page_title_workflows")));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const versionIdParam = searchParams.get("versionId");
  const [page, setPage] = useState(1);
  const [auditInstanceId, setAuditInstanceId] = useState<number | null>(null);
  const [issueFilter, setIssueFilter] = useState<WorkflowIssueFilter>("hide");

  const { data, isLoading, isFetching } = useGetWorkflowInstancesByIdQuery(
    {
      id: id!,
      versionId: versionIdParam ?? undefined,
      issueFilter,
    },
    { skip: !id },
  );

  const instances = data?.data ?? [];
  const workflowName =
    instances[0]?.workflowTemplate?.name ??
    t("company:workflows.workflow_requests_fallback");
  const templateId = instances[0]?.workflowTemplate?.id ?? id;

  const requestDetailHref = (
    entityType: string,
    entityId: number,
  ): string | null => {
    if (entityType === ENUM_MODULE_TYPE.LEAVE_MANAGEMENT) {
      return PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.LEAVES_SUMMARY(
        entityId,
      );
    }
    return null;
  };

  // Open the workflow in the Studio builder (version snapshots aren't rendered
  // in Studio yet, so we open the live template).
  const buildWorkflowViewHref = (entityType?: string | null): string | null => {
    if (!templateId) return null;
    return COMPANY_STUDIO_PATHS.WORKFLOW_BUILDER(
      getCategoryForModule(entityType ?? ""),
      templateId,
    );
  };

  const rows = instances.map((item) => {
    const requestHref = requestDetailHref(item.entityType, item.entityId);
    const workflowHref = buildWorkflowViewHref(item.entityType);
    return {
      leaveId: item.entityId,
      requestHref,
      requestedBy: (
        <div className="flex items-center gap-2">
          <Avatar className="size-9 bg-primary/10 border border-white shrink-0">
            <AvatarImage
              src={item.requester?.profileImage ?? ""}
              alt={item.requester?.firstName}
            />
            <AvatarFallback>
              <UserIcon size={14} className="text-white" />
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-gray-800">
            {item.requester?.firstName} {item.requester?.lastName}
          </span>
        </div>
      ),
      currentApprover: (
        <div className="flex items-center gap-2">
          <Avatar className="size-9 bg-primary/10 border border-white shrink-0">
            <AvatarImage
              src={item.currentApprover?.profileImage ?? ""}
              alt={item.currentApprover?.firstName}
            />
            <AvatarFallback>
              <UserIcon size={14} className="text-white" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-xs text-gray-800">
              {item.currentApprover?.firstName} {item.currentApprover?.lastName}
            </span>
            {item.currentApprover &&
              item.currentApprover.isActive === false && (
                <Badge
                  variant="warning"
                  dot={false}
                  className="text-[10px] mt-0.5 w-fit"
                >
                  <AlertTriangle className="size-3" />
                  {t("company:workflows.inactive_auto_reassigning")}
                </Badge>
              )}
          </div>
        </div>
      ),
      requestedAt: (
        <span className="text-xs text-gray-700">
          {item.startedAt
            ? formatDate(new Date(item.startedAt), "dd MMM yyyy, hh:mm a")
            : "-"}
        </span>
      ),
      deadline: (
        <span className="text-xs text-gray-700">
          {item.currentStepDeadline
            ? formatDate(new Date(item.currentStepDeadline), "MMM dd, yyyy")
            : "-"}
        </span>
      ),
      version: item.workflowTemplateVersion?.versionNumber ? (
        <span
          className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
          data-tooltip-id="hrm-tooltip"
          data-tooltip-content={
            item.workflowTemplateVersion.publishedAt
              ? t("company:workflows.published_at", {
                  when: formatDate(
                    new Date(item.workflowTemplateVersion.publishedAt),
                    "dd MMM yyyy, hh:mm a",
                  ),
                })
              : t("company:workflows.pinned_workflow_version")
          }
        >
          {t("company:workflows.version_short", {
            number: item.workflowTemplateVersion.versionNumber,
          })}
        </span>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
      status: (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <WorkflowStatusPill status={item.status} />
            {item.hasNotificationIssue && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1 py-0.5"
                data-tooltip-id="hrm-tooltip"
                data-tooltip-content={t(
                  "common:a11y.one_or_more_notifications_for_this_request_failed_to_sen",
                )}
              >
                <AlertTriangle className="size-3" />
                {t("company:workflows.notify_stuck")}
              </span>
            )}
          </div>
          {item.lastErrorMessage && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-red-700 max-w-[200px] truncate cursor-help"
              data-tooltip-id="hrm-tooltip"
              data-tooltip-content={`${t("company:workflows.last_failure_msg", { message: item.lastErrorMessage })}${item.errorRetryCount ? t("company:workflows.attempt_n_of_3", { count: item.errorRetryCount }) : ""}`}
            >
              <AlertOctagon className="size-3 shrink-0" />
              <span className="truncate">{item.lastErrorMessage}</span>
            </span>
          )}
        </div>
      ),
      actions: (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {requestHref && (
            <Link
              to={requestHref}
              data-tooltip-id="hrm-tooltip"
              data-tooltip-content={t("common:shared_ui.view_request")}
              className={buttonVariants({
                variant: "secondary",
                size: "icon-sm",
              })}
            >
              <ViewIcon className="w-4 h-4 text-primary" />
            </Link>
          )}
          {workflowHref && (
            <Link
              to={workflowHref}
              data-tooltip-id="hrm-tooltip"
              data-tooltip-content={t("common:a11y.view_workflow")}
              className={buttonVariants({
                variant: "ghost",
                size: "icon",
                className: "gap-2 text-sm!",
              })}
            >
              <GitMerge className="w-4 h-4 text-info" />
            </Link>
          )}
          <button
            type="button"
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={t(
              "company:leave_management.activity_history",
            )}
            onClick={() => setAuditInstanceId(item.id)}
            className={buttonVariants({
              variant: "ghost",
              size: "icon",
              className: "gap-2 text-sm!",
            })}
          >
            <History className="w-4 h-4 text-amber-600" />
          </button>
        </div>
      ),
    };
  });

  return (
    <Card className="border-none shadow-none p-6">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 px-0 pt-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0 hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={18} className="text-primary rtl:rotate-180" />
          </button>
          <CardTitle className="text-lg font-semibold">
            {workflowName}
          </CardTitle>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-500">
            {t("company:workflows.issues_label")}
          </span>
          {ISSUE_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                setIssueFilter(opt);
                setPage(1);
              }}
              className={
                "px-2.5 py-1 rounded border transition-colors " +
                (issueFilter === opt
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-border hover:bg-gray-50")
              }
              data-tooltip-id="hrm-tooltip"
              data-tooltip-content={
                opt === "hide"
                  ? t("company:workflows.hide_problems_hint")
                  : opt === "show"
                    ? t("company:workflows.include_problems_hint")
                    : t("company:workflows.problems_only_hint")
              }
            >
              {opt === "hide"
                ? t("company:workflows.hide_problems")
                : opt === "show"
                  ? t("company:workflows.include_problems")
                  : t("company:workflows.problems_only")}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="px-0">
        <Table
          columns={columns}
          data={rows}
          pagination={{
            totalPages: Math.ceil(instances.length / 10),
            currentPage: page,
            onPageChange: (p) => setPage(p),
          }}
          loading={isLoading || isFetching}
          onRowClick={(row) => {
            if (row.requestHref) navigate(row.requestHref);
          }}
        />
      </CardContent>
      <WorkflowAuditDialog
        open={auditInstanceId !== null}
        onOpenChange={(open) => !open && setAuditInstanceId(null)}
        instanceId={auditInstanceId}
      />
    </Card>
  );
};

export default WorkflowRequestsTable;
